import React, { useState, useEffect } from 'react';
import InfoModal from './overlays/InfoModal.js';
import SoundSettingsModal from './overlays/SoundSettingsModal.js';
import InitialMuteModal from './overlays/InitialMuteModal.js';
import { getUILoc } from '../utils/cardUtils.js';
import audioManager from '../utils/AudioManager.js';
import { BGMCategory } from '../gameLogic/constants.js';

const ASCII_ART_TITLE = `
                                              s                               ..
    .uef^"          oe           .           :8                              888B.     .n~~%x.
  :d88E           .@88           E          .88           u.      .u    .   48888E   x88X   888.
  \`888E       ==*88888        .x+E:..      :888ooo  ...ue888b   .d88B :@c   '8888'  X888X   88888X
   888E .z8k     88888      u8~  E  \`b.  -*8888888  888R Y888r ="8888f8888r  Y88F  X8888X   88888f
   888E  888E    88888     88N.  E'8888~   8888     888R I888>   4888>        4    88888X   8888f
   888E  888E    88888     88888b&.\`""\`    8888     888R I888>   4888>        4    88888X   8888"
   888E  888E    88888     '88888888e.    .8888Lu= u8888cJ888   .d888L .+     .    48888X   8888"
   888E  888E    88888       "*8888888N   ^%888*    "*888*P"    ^"8888*"     u8N.   ?888X   8888"
  m888N= 888>    88888      uu. ^8*8888E    'Y"       'Y"          "Y"      "*88%    "88X   88*\`
   \`Y"   888 '**%%%%%%**   @888L E   98~                                      ""       ^"==="\`
        J88"              '8888~ E  .*"
        @%                 \`*.   E  .*"
      :"                     \`~==R=~\`
`;

let initialMuteShown = false;

const TitleScreen = ({ onScreenChange, onShowRules, onShowCredits, onShowChangelog, version, language, onLanguageChange }) => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [showInitialMute, setShowInitialMute] = useState(!initialMuteShown);
  const [activeRun, setActiveRun] = useState(null);
  const [showLimitVerification, setShowLimitVerification] = useState(false);

  // 行単位のグリッチ制御用
  useEffect(() => {
    const lines = document.querySelectorAll('.glitch-line');
    if (lines.length === 0) return;

    const interval = setInterval(() => {
      // ランダムに数行選んでノイズを適用
      const numLinesToGlitch = Math.floor(Math.random() * 3) + 1;
      for (let i = 0; i < numLinesToGlitch; i++) {
        const randomIndex = Math.floor(Math.random() * lines.length);
        const line = lines[randomIndex];
        line.classList.add('line-noise');
        // アニメーション時間分待ってからクラスを削除
        setTimeout(() => line.classList.remove('line-noise'), 100); 
      }
    }, 6500); // 2秒ごとに発生

    return () => clearInterval(interval);
  }, []);

  // 文字単位のグリッチ制御用
  useEffect(() => {
    const chars = document.querySelectorAll('.glitch-char');
    if (chars.length === 0) return;

    // --- 調整可能パラメータ ---
    const GLITCH_INTERVAL = 545; // グリッチが発生する周期(ms)
    const GLITCH_THRESHOLD = 0.99; // 確率
    // -----------------------

    const interval = setInterval(() => {
      chars.forEach(char => {
        if (Math.random() > GLITCH_THRESHOLD) {
          const effect = ['flicker', 'shift-up', 'shift-down', 'color-shift', 'glitch-size'][Math.floor(Math.random() * 5)];
          char.classList.add(effect);
          setTimeout(() => char.classList.remove(effect), 100 + Math.random() * 200);
        }
      });
    }, GLITCH_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // もしすでに初期ミュート選択済みの場合はBGMを開始する
    if (initialMuteShown) {
      audioManager.ensureCategoryBGM(BGMCategory.BASIC);
    }
    
    // 世の常のアクティブランをチェック
    try {
      const saved = localStorage.getItem('historio_yonotsune_active_run');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.isActive) {
          setActiveRun(parsed.currentRun);
        }
      }
    } catch(e) {}
  }, []);

  const handleInitialMuteSelect = (muted) => {
    initialMuteShown = true;
    setShowInitialMute(false);
    audioManager.setMute(muted);

    // Always attempt init to start audio context
    audioManager.init().then(() => {
        if (!muted) {
            setShowSoundSettings(true);
            audioManager.ensureCategoryBGM(BGMCategory.BASIC);
        }
    });
  };

  const handleMenuClick = (action) => {
    audioManager.playSE('select');
    action();
  };

  const renderMenuOptions = () => {
    if (showLimitVerification) {
        return (
            <>
                <button
                  className="menu-button start-button"
                  onClick={() => handleMenuClick(() => onScreenChange('yonotsune'))}
                >
                  <span className="button-glitch"></span>
                  {getUILoc('BUTTONS.YONOTSUNE_MODE')}
                </button>
                <button
                  className="menu-button start-button"
                  onClick={() => handleMenuClick(() => onScreenChange('hypothesisVerification'))}
                >
                  <span className="button-glitch"></span>
                  {getUILoc('BUTTONS.HYPOTHESIS_VERIFICATION')}
                </button>
                <button
                  className="menu-button"
                  onClick={() => handleMenuClick(() => setShowLimitVerification(false))}
                >
                  {getUILoc('BUTTONS.BACK')}
                </button>
            </>
        );
    }

    return (
      <>
        {activeRun && (
          <button
            className="menu-button start-button"
            onClick={() => handleMenuClick(() => onScreenChange('yonotsune'))}
          >
            <span className="button-glitch"></span>
            {getUILoc('YONOTSUNE.RESUME_RUN').replace('{count}', activeRun.matchedCount + 1)}
          </button>
        )}

        <button 
          className="menu-button start-button"
          onClick={() => handleMenuClick(() => onScreenChange('deckSelection'))}
        >
          <span className="button-glitch"></span>
          {getUILoc('BUTTONS.START_GAME')}
        </button>

        <button 
          className="menu-button"
          onClick={() => handleMenuClick(() => setShowLimitVerification(true))}
        >
          {getUILoc('BUTTONS.LIMIT_VERIFICATION')}
        </button>

        <button 
          className="menu-button"
          onClick={() => handleMenuClick(() => onScreenChange('deckBuilder'))}
        >
          {getUILoc('BUTTONS.DECK_BUILDER')}
        </button>

        <button 
          className="menu-button"
          onClick={() => handleMenuClick(() => onScreenChange('cardLibrary'))}
        >
          {getUILoc('BUTTONS.ARCHIVE')}
        </button>

        <button 
          className="menu-button"
          onClick={() => handleMenuClick(onShowRules)}
        >
          {getUILoc('BUTTONS.RULES')}
        </button>

        <button 
          className="menu-button credits-button"
          onClick={() => handleMenuClick(onShowCredits)}
        >
          {getUILoc('BUTTONS.TOOLS')}
        </button>
      </>
    );
  };

  const renderASCIIArt = () => {
    return (
      <div className="ascii-container">
        {ASCII_ART_TITLE.split('\n').map((line, lineIndex) => (
          <div key={lineIndex} className="glitch-line">
            {line.split('').map((char, charIndex) => (
              <span key={charIndex} className="glitch-char">{char}</span>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="title-screen">
      <div className="title-bg-animation"></div>
      <div className="title-scanline"></div>

      <header className="title-header">
        <div className="header-right">
          <button 
            className="sound-settings-trigger"
            onClick={() => {
                audioManager.playSE('select');
                setShowSoundSettings(true);
            }}
            title={getUILoc('SOUND_SETTINGS.TITLE')}
          >
            {audioManager.isMuted ? '🔇' : '🔊'}
          </button>
          <div className="language-selector-wrapper">
            <span className="language-icon" role="img" aria-label="Language selection">🌐</span>
            <select 
              className="language-select" 
              value={language} 
              onChange={(e) => {
                audioManager.playSE('select');
                onLanguageChange(e.target.value);
              }}
            >
              <option value="ja">日本語</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
      </header>

      <div className="title-content">
        <div className="glitch-container ascii-art no-select">
          {renderASCIIArt()}
        </div>

        <div className="title-sub-info">
          <div className="version-info">
            <button className="version-button" onClick={() => handleMenuClick(onShowChangelog)}>
              SYSTEM v{version}
            </button>
          </div>
        </div>

        <div className="menu-options">
          {renderMenuOptions()}
        </div>
      </div>

      <footer className="title-footer">
        <div className="footer-content">
          <div className="footer-top-row">
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSczk2333RJtqkKMM6TCIpu-LSBAYcfmoTOudi_q-htChW0yiw/viewform?usp=pp_url&entry.557884954=h1%24tor%210" target="_blank" rel="noopener noreferrer" className="disclaimer-button" onClick={() => audioManager.playSE('select')}>
              {getUILoc('BUTTONS.CONTACT')}
            </a>
            <a href="https://forms.gle/8s3GpBh9gsQHwU3o9" target="_blank" rel="noopener noreferrer" className="disclaimer-button" onClick={() => audioManager.playSE('select')}>
              {getUILoc('BUTTONS.SURVEY')}
            </a>
            <button className="disclaimer-button" onClick={() => handleMenuClick(() => setShowDisclaimer(true))}>
              {getUILoc('BUTTONS.DISCLAIMER')}
            </button>
          </div>
          <div className="footer-bottom-row">
            <div className="copyright">
              &copy; 2025 - {new Date().getFullYear()} toksann HISTORIO. All Rights Reserved.
            </div>
          </div>
        </div>
      </footer>

      <InfoModal 
        isOpen={showDisclaimer} 
        onClose={() => {
          audioManager.playSE('cancel');
          setShowDisclaimer(false);
        }} 
        title={getUILoc('DISCLAIMER.TITLE')}
        message={getUILoc('DISCLAIMER.BODY')}
      />

      <SoundSettingsModal 
        isOpen={showSoundSettings} 
        onClose={() => {
          audioManager.playSE('cancel');
          setShowSoundSettings(false);
        }}
      />

      <InitialMuteModal 
        isOpen={showInitialMute} 
        onSelect={handleInitialMuteSelect}
      />
    </div>
  );
};

export default TitleScreen;
